import { Link } from "react-router-dom";
import {
  Activity,
  Bot,
  CheckCircle2,
  Clock,
  PhoneCall,
  Plus,
  XCircle,
  AlertCircle,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth/store";
import MyMinutesStrip from "@/features/customers/MyMinutesStrip";
import { cn } from "@/lib/utils";
import {
  useAgents,
  useCallStats,
  useRecentCalls,
} from "@/features/dashboard/hooks";
import type { Call } from "@/features/calls/api";

function formatDuration(seconds: number) {
  if (!seconds || seconds < 0) return "0s";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

function formatNumber(n: number | undefined | null) {
  if (n == null) return "—";
  return new Intl.NumberFormat().format(n);
}

function formatDateTime(iso?: string | null) {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t) || t === 0) return "—";
  return new Date(t).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Pick the most meaningful timestamp for display + sorting.
// Order of preference: started_at → created_at → ended_at.
function callTimestamp(c: { started_at?: string | null; created_at?: string; ended_at?: string | null }) {
  return c.started_at || c.created_at || c.ended_at || null;
}

function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  loading,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ComponentType<{ className?: string }>;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <Icon className="size-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-20" />
        ) : (
          <div className="text-2xl font-semibold tabular-nums">{value}</div>
        )}
        {subtitle && (
          <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );
}

function CallStatusBadge({ status }: { status: Call["status"] }) {
  const map: Record<string, { label: string; cls: string; Icon: typeof CheckCircle2 }> = {
    completed: {
      label: "Completed",
      cls: "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400",
      Icon: CheckCircle2,
    },
    failed: {
      label: "Failed",
      cls: "text-rose-600 bg-rose-50 dark:bg-rose-950/40 dark:text-rose-400",
      Icon: XCircle,
    },
    no_answer: {
      label: "No answer",
      cls: "text-amber-600 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-400",
      Icon: AlertCircle,
    },
    in_progress: {
      label: "In progress",
      cls: "text-blue-600 bg-blue-50 dark:bg-blue-950/40 dark:text-blue-400",
      Icon: Activity,
    },
    pending: {
      label: "Pending",
      cls: "text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300",
      Icon: Clock,
    },
  };
  const entry = map[status] ?? {
    label: status,
    cls: "text-muted-foreground bg-muted",
    Icon: Activity,
  };
  const { label, cls, Icon } = entry;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
        cls,
      )}
    >
      <Icon className="size-3" />
      {label}
    </span>
  );
}

export default function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const firstName = user?.first_name || user?.email?.split("@")[0] || "there";

  const agentsQ = useAgents();
  const statsQ = useCallStats();
  const recentQ = useRecentCalls(5);

  const agents = agentsQ.data ?? [];
  const activeAgents = agents.filter((a) => a.status === "active").length;
  const stats = statsQ.data;
  // Trust server order (Call.Meta.ordering = ['-created_at']) — newest
  // created first. Just slice; matches the legacy dashboard exactly.
  const calls = (recentQ.data ?? []).slice(0, 5);

  const totalMinutes = stats
    ? Math.floor((stats.total_duration_seconds ?? 0) / 60)
    : 0;
  const successRate =
    stats?.success_rate != null ? `${stats.success_rate}%` : "—";

  return (
    <div className="space-y-8">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Welcome back, {firstName}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Here&apos;s what&apos;s happening with your voice agents today.
          </p>
        </div>
        <Button asChild>
          <Link to="/agents">
            <Plus className="size-4" />
            Create Agent
          </Link>
        </Button>
      </div>

      {/* Customers only: their allowance, where they will actually see it. */}
      <MyMinutesStrip />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Active agents"
          value={agentsQ.isLoading ? "" : activeAgents}
          subtitle={agentsQ.data ? `of ${agents.length} total` : undefined}
          icon={Bot}
          loading={agentsQ.isLoading}
        />
        <StatCard
          title="Total calls"
          value={statsQ.isLoading ? "" : formatNumber(stats?.total_calls)}
          subtitle="This month"
          icon={PhoneCall}
          loading={statsQ.isLoading}
        />
        <StatCard
          title="Call minutes"
          value={statsQ.isLoading ? "" : formatNumber(totalMinutes)}
          subtitle="Total duration"
          icon={Clock}
          loading={statsQ.isLoading}
        />
        <StatCard
          title="Success rate"
          value={statsQ.isLoading ? "" : successRate}
          subtitle="Completed calls"
          icon={Activity}
          loading={statsQ.isLoading}
        />
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle>Recent calls</CardTitle>
            <CardDescription>Last 5 calls across all agents</CardDescription>
          </div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/calls">View all</Link>
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {recentQ.isLoading && (
            <div className="space-y-4 p-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                  <Skeleton className="h-6 w-20 rounded-full" />
                </div>
              ))}
            </div>
          )}

          {!recentQ.isLoading && calls.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
              <PhoneCall className="size-8 text-muted-foreground" />
              <p className="text-sm font-medium">No calls yet</p>
              <p className="text-xs text-muted-foreground">
                Create an agent and start making calls to see activity here.
              </p>
            </div>
          )}

          {!recentQ.isLoading && calls.length > 0 && (
            <ul className="divide-y">
              {calls.map((call) => (
                <li
                  key={call.id}
                  className="flex items-center justify-between gap-4 px-6 py-3.5"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {call.from_number}{" "}
                      <span className="text-muted-foreground">→</span>{" "}
                      {call.to_number}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">
                      {call.agent_name || call.agent?.name || "No agent"} ·{" "}
                      {formatDateTime(callTimestamp(call))}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <span className="text-xs tabular-nums text-muted-foreground">
                      {formatDuration(call.duration_seconds)}
                    </span>
                    <CallStatusBadge status={call.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {(agentsQ.isError || statsQ.isError || recentQ.isError) && (
        <p className="text-xs text-muted-foreground">
          Some dashboard data couldn&apos;t be loaded. Check your connection or
          try refreshing.
        </p>
      )}
    </div>
  );
}
